import { computed, ref } from "vue";
import { useClamp, useStep } from "@/composables/math";
import { useUserLocalStorage } from "@/composables/userLocalStorage";
import { defineScopedStore } from "@/stores/scopedStore";
import { UndoRedoAction } from "./undoRedoAction";
export { LazyUndoRedoAction, UndoRedoAction } from "./undoRedoAction";
export class ActionOutOfBoundsError extends Error {
    constructor(action, bounds) {
        super(`The action "${action.name}" is not in the ${bounds} stack`);
        Object.defineProperty(this, "action", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.action = action;
    }
}
export const useUndoRedoStore = defineScopedStore("undoRedoStore", () => {
    const undoActionStack = ref([]);
    const redoActionStack = ref([]);
    const minUndoActions = ref(10);
    const maxUndoActions = ref(10000);
    const savedUndoActionsValue = useUserLocalStorage(`undoRedoStore-savedUndoActions`, 100);
    const savedUndoActions = useClamp(useStep(savedUndoActionsValue), minUndoActions, maxUndoActions);
    /** names of actions which were deleted due to savedUndoActions being exceeded */
    const deletedActions = ref([]);
    function $reset() {
        undoActionStack.value.forEach((action) => action.destroy());
        undoActionStack.value = [];
        deletedActions.value = [];
        minUndoActions.value = 10;
        maxUndoActions.value = 10000;
        clearRedoStack();
    }
    function undo() {
        flushLazyAction();
        const action = undoActionStack.value.pop();
        if (action !== undefined) {
            action.undo();
            redoActionStack.value.push(action);
        }
    }
    function redo() {
        const action = redoActionStack.value.pop();
        if (action !== undefined) {
            action.redo();
            undoActionStack.value.push(action);
        }
    }
    function applyAction(action) {
        var _a;
        flushLazyAction();
        action.run();
        clearRedoStack();
        undoActionStack.value.push(action);
        while (undoActionStack.value.length > savedUndoActions.value && undoActionStack.value.length > 0) {
            const action = undoActionStack.value.shift();
            deletedActions.value.push((_a = action === null || action === void 0 ? void 0 : action.name) !== null && _a !== void 0 ? _a : "unnamed action");
            action === null || action === void 0 ? void 0 : action.destroy();
        }
    }
    function clearRedoStack() {
        redoActionStack.value.forEach((action) => action.destroy());
        redoActionStack.value = [];
    }
    /**
     * constructs a new action inline
     *
     * @example
     * action()
     *     .onRun(() => console.log("run"))
     *     .onUndo(() => console.log("undo"))
     *     .apply();
     */
    function action() {
        return new FactoryAction((action) => applyAction(action));
    }
    let lazyActionTimeout = undefined;
    /** action which is currently queued to run */
    const pendingLazyAction = ref(null);
    /**
     * Queues an action to be applied after a delay.
     * The action is applied immediately, should another action be applied, or be queued.
     * You can read the `pendingLazyAction` state, or `isQueued`, to find out if the action was applied.
     *
     * `flushLazyAction` runs the pending lazy action immediately.
     *
     * `setLazyActionTimeout` can be used to extend the timeout.
     *
     * @param action action to queue
     * @param timeout when to run the action in milliseconds. default to 1000 milliseconds
     */
    function applyLazyAction(action, timeout = 1000) {
        flushLazyAction();
        clearRedoStack();
        pendingLazyAction.value = action;
        action.queued();
        lazyActionTimeout = setTimeout(() => flushLazyAction(), timeout);
    }
    function clearLazyAction() {
        clearTimeout(lazyActionTimeout);
        pendingLazyAction.value = null;
    }
    function flushLazyAction() {
        clearTimeout(lazyActionTimeout);
        if (pendingLazyAction.value) {
            const action = pendingLazyAction.value;
            clearLazyAction();
            applyAction(action);
        }
    }
    function setLazyActionTimeout(timeout = 1000) {
        clearTimeout(lazyActionTimeout);
        lazyActionTimeout = setTimeout(() => flushLazyAction(), timeout);
    }
    const isQueued = computed(() => (action) => action && pendingLazyAction.value === action);
    const nextUndoAction = computed(() => undoActionStack.value[undoActionStack.value.length - 1]);
    const nextRedoAction = computed(() => redoActionStack.value[redoActionStack.value.length - 1]);
    const hasUndo = computed(() => Boolean(nextUndoAction.value));
    const hasRedo = computed(() => Boolean(nextRedoAction.value));
    const undoText = computed(() => {
        if (!nextUndoAction.value) {
            return "Nothing to undo";
        }
        else if (!nextUndoAction.value.name) {
            return "Undo";
        }
        else {
            return `Undo ${nextUndoAction.value.name}`;
        }
    });
    const redoText = computed(() => {
        if (!nextRedoAction.value) {
            return "Nothing to redo";
        }
        else if (!nextRedoAction.value.name) {
            return "Redo";
        }
        else {
            return `Redo ${nextRedoAction.value.name}`;
        }
    });
    function rollBackTo(action) {
        flushLazyAction();
        const undoSet = new Set(undoActionStack.value);
        if (!undoSet.has(action)) {
            throw new ActionOutOfBoundsError(action, "undo");
        }
        while (nextRedoAction.value !== action) {
            undo();
        }
    }
    function rollForwardTo(action) {
        flushLazyAction();
        const redoSet = new Set(redoActionStack.value);
        if (!redoSet.has(action)) {
            throw new ActionOutOfBoundsError(action, "redo");
        }
        while (nextUndoAction.value !== action) {
            redo();
        }
    }
    return {
        undoActionStack,
        redoActionStack,
        minUndoActions,
        maxUndoActions,
        savedUndoActions,
        deletedActions,
        undo,
        redo,
        applyAction,
        action,
        applyLazyAction,
        clearLazyAction,
        flushLazyAction,
        setLazyActionTimeout,
        isQueued,
        pendingLazyAction,
        nextUndoAction,
        nextRedoAction,
        undoText,
        redoText,
        hasUndo,
        hasRedo,
        $reset,
        rollBackTo,
        rollForwardTo,
    };
});
class FactoryAction extends UndoRedoAction {
    constructor(applyCallback) {
        super();
        Object.defineProperty(this, "applyCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "runCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "undoCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "redoCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "destroyCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.applyCallback = applyCallback;
    }
    onRun(callback) {
        this.runCallback = callback;
        return this;
    }
    onUndo(callback) {
        this.undoCallback = callback;
        return this;
    }
    onRedo(callback) {
        this.redoCallback = callback;
        return this;
    }
    onDestroy(callback) {
        this.destroyCallback = callback;
        return this;
    }
    setName(name) {
        this.name = name;
        return this;
    }
    apply() {
        this.applyCallback(this);
    }
    run() {
        this.runCallback ? this.runCallback() : null;
    }
    undo() {
        this.undoCallback ? this.undoCallback() : null;
    }
    redo() {
        this.redoCallback ? this.redoCallback() : this.run();
    }
    destroy() {
        this.destroyCallback ? this.destroyCallback() : null;
    }
}
